import { AppState } from '../../../redux/reducers';
import { connect, useDispatch, useSelector } from 'react-redux';
import { MedicalInsuranceAgreement } from '../../../models/Entities/MedicalInsuranceAgreements/MedicalInsuranceAgreement';
import { MedicalInsurance } from '../../../models/Entities/MedicalInsurances/MedicalInsurance';
import { MedicalInsurancePlan } from '../../../models/Entities/MedicalInsurances/MedicalInsurancePlan';
import { useEffect, useMemo, useState } from 'react';
import { PrimaryButton } from '@fluentui/react';
import {
  Badge,
  SearchBox,
  Spinner,
  Tooltip
} from '@fluentui/react-components';
import {
  Add24Regular,
  ClipboardTaskListLtr20Regular,
  PeopleTeam16Regular,
  Person16Regular,
  CalendarLtr16Regular,
  LockClosed16Regular
} from '@fluentui/react-icons';
import MedicalServiceMultiSelectorComponent, {
  SelectedMedicalServiceType
} from '../../MedicalServices/MedicalServiceSelector/MedicalServiceMultiSelectorComponent';
import { AgreementService } from '../../../models/Entities/AgreementServices/AgreementServices';
import { useBoolean } from '@fluentui/react-hooks';
import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import AgreementServiceDetailItemComponent from '../../AgreementServiceDetails/AgreementServiceDetailItem/AgreementServiceDetailItemComponent';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

//store
import * as MedicalServiceStore from '../../../redux/middleware/MedicalServicesMiddleware';
import * as MedicalInsuranceStore from '../../../redux/middleware/MedicalInsuranceMiddleware';
import * as AgreementServicesStore from '../../../redux/middleware/AgreementServicesMiddleware';

// functions
import * as CommonFunction from '../../../functions/common';

// styles
import './AgreementServiceListComponent.css';

////i18Next
import { useTranslation } from 'react-i18next';

export interface IProps {
  AgreementId: number;
}

export const AgreementServiceListComponent = (props: IProps) => {
  const [filter, setFilter] = useState('');

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);

  // states
  const AgreementState = useSelector(
    (state: AppState) => state.MedicalInsuranceAgreements
  );
  const MedicalInsuranceState = useSelector(
    (state: AppState) => state.MedicalInsurances
  );
  const AgreementServiceState = useSelector(
    (state: AppState) => state.AgreementServices
  );

  // useTranslation (T)
  const [t] = useTranslation();

  // effects
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      mapDispatchToProps.GetAllAgreementServiceByAgreementId(
        props.AgreementId,
        1,
        1000
      )
    );
  }, [props.AgreementId, dispatch]);

  // Derived from the store instead of mirrored into local state: the previous
  // version kept Agreement in state AND in its own effect's dependency list.
  const Agreement: MedicalInsuranceAgreement | undefined = useMemo(
    () =>
      AgreementState?.list.find(
        (item: MedicalInsuranceAgreement) => item.id === props.AgreementId
      ),
    [AgreementState, props.AgreementId]
  );

  const Plans: MedicalInsurancePlan[] = useMemo(
    () =>
      MedicalInsuranceState?.list
        .find(
          (insurance: MedicalInsurance) =>
            insurance.id === Agreement?.medicalInsuranceId
        )
        ?.plans.slice()
        .sort(CommonFunction.DynamicSort('title')) || [],
    [MedicalInsuranceState, Agreement?.medicalInsuranceId]
  );

  const allServices: AgreementService[] = useMemo(
    () => AgreementServiceState?.list || [],
    [AgreementServiceState]
  );

  // The search box was decorative before — it is wired now.
  const visibleServices = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const sorted = allServices
      .slice()
      .sort((a, b) =>
        (a.medicalService?.serviceNameReference || '').localeCompare(
          b.medicalService?.serviceNameReference || '',
          undefined,
          { sensitivity: 'base', numeric: true }
        )
      );

    if (!term) return sorted;

    return sorted.filter((service: AgreementService) => {
      const name = (service.medicalService?.serviceNameReference || '').toLowerCase();
      const comments = (service.medicalService?.comments || '').toLowerCase();
      const snomed = (service.medicalService?.snowmedName || '').toLowerCase();
      return name.includes(term) || comments.includes(term) || snomed.includes(term);
    });
  }, [allServices, filter]);

  // events
  const HandleOnAddServicesClick = () => {
    openPanel();
  };

  const handleClosePanel = () => {
    dismissPanel();
  };

  const onSelectService = (service: AvailableMedicalService) => {
    if (!Agreement) return;
    dispatch(
      mapDispatchToProps.AddAgreementService({
        medicalServiceId: service.id,
        agreementId: Agreement.id
      })
    );
  };

  const onUnselectService = (service: SelectedMedicalServiceType) => {
    dispatch(mapDispatchToProps.DeleteAgreementService(service.ReferenceId));
  };

  const isLoading = AgreementServiceState?.isLoadingAll || false;
  const hasError = AgreementServiceState?.failOnLoadingAll || false;

  // Traits already carried by the model, surfaced as chips so the row says what
  // kind of service it is without opening anything.
  const renderServiceTraits = (service: AvailableMedicalService) => {
    const traits = [
      {
        key: '3rd',
        active: service.serviceProvidedBy3rdParty,
        icon: <PeopleTeam16Regular />,
        label: t('agreementServiceListComponent.traits.thirdParty')
      },
      {
        key: 'self',
        active: service.enablePatientSelfManagement,
        icon: <Person16Regular />,
        label: t('agreementServiceListComponent.traits.selfManagement')
      },
      {
        key: 'appointment',
        active: service.enableforAppointment,
        icon: <CalendarLtr16Regular />,
        label: t('agreementServiceListComponent.traits.appointment')
      },
      {
        key: 'admin',
        active: service.onlyForAdministrativeUse,
        icon: <LockClosed16Regular />,
        label: t('agreementServiceListComponent.traits.administrative')
      }
    ].filter((trait) => trait.active);

    if (traits.length === 0) return null;

    return (
      <span className="asl-traits">
        {traits.map((trait) => (
          <Tooltip
            key={trait.key}
            content={trait.label}
            relationship="label"
            withArrow
          >
            <span className="asl-trait" aria-label={trait.label}>
              {trait.icon}
            </span>
          </Tooltip>
        ))}
      </span>
    );
  };

  const renderBody = () => {
    if (isLoading && allServices.length === 0) {
      return (
        <div className="asl-state">
          <Spinner size="tiny" />
          <span>{t('agreementServiceListComponent.state.loading')}</span>
        </div>
      );
    }

    if (Plans.length === 0) {
      return (
        <div className="asl-state asl-state--empty">
          {t('agreementServiceListComponent.state.noPlans')}
        </div>
      );
    }

    if (allServices.length === 0) {
      return (
        <div className="asl-state asl-state--empty">
          {t('agreementServiceListComponent.state.noServices')}
        </div>
      );
    }

    if (visibleServices.length === 0) {
      return (
        <div className="asl-state asl-state--empty">
          {t('agreementServiceListComponent.state.noMatches', { term: filter })}
        </div>
      );
    }

    return (
      <div className="asl-table-wrap">
        <table className="asl-table">
          <thead>
            <tr>
              <th className="asl-th asl-th--service asl-sticky-col">
                {t('agreementServiceListComponent.service.services')}
              </th>
              {Plans.map((plan: MedicalInsurancePlan) => (
                <th key={plan.id} className="asl-th asl-th--plan">
                  <span className="asl-plan-name" title={plan.title}>
                    {plan.title}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleServices.map((service: AgreementService) => (
              <tr key={service.id} className="asl-tr">
                <td className="asl-td asl-td--service asl-sticky-col">
                  <div className="asl-service">
                    <span className="asl-service__name">
                      {service.medicalService?.serviceNameReference}
                    </span>
                    {service.medicalService?.comments &&
                      service.medicalService.comments.length > 0 && (
                        <span
                          className="asl-service__comments"
                          title={service.medicalService.comments}
                        >
                          {service.medicalService.comments}
                        </span>
                      )}
                    {service.medicalService &&
                      renderServiceTraits(service.medicalService)}
                  </div>
                </td>
                {Plans.map((plan: MedicalInsurancePlan) => (
                  <td key={plan.id} className="asl-td asl-td--plan">
                    <AgreementServiceDetailItemComponent
                      Plan={plan}
                      Service={service}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="asl-card">
      <header className="asl-card__header">
        <span className="asl-card__icon">
          <ClipboardTaskListLtr20Regular />
        </span>
        <h3 className="asl-card__title">
          {t('agreementServiceListComponent.service.services')}
        </h3>
        <Badge appearance="tint" color="informative" shape="rounded">
          {filter.trim() && visibleServices.length !== allServices.length
            ? `${visibleServices.length} / ${allServices.length}`
            : allServices.length}
        </Badge>
        {isLoading && allServices.length > 0 && (
          <Spinner size="extra-tiny" className="asl-card__spinner" />
        )}

        <div className="asl-card__actions">
          <SearchBox
            className="asl-search"
            size="medium"
            placeholder={t('agreementServiceListComponent.service.searchService')}
            value={filter}
            onChange={(_, data) => setFilter(data.value || '')}
          />
          <PrimaryButton
            onClick={HandleOnAddServicesClick}
            text={t('agreementServiceListComponent.service.addMedicalService')}
            onRenderIcon={() => <Add24Regular />}
            className="asl-add-btn"
            disabled={!Agreement}
          />
        </div>
      </header>

      {hasError && AgreementServiceState?.error && (
        <ErrorMessageComponent Error={AgreementServiceState.error} />
      )}

      {renderBody()}

      <MedicalServiceMultiSelectorComponent
        SelectedServices={
          (allServices.map((service: AgreementService) => ({
            medicalServiceId: service.medicalServiceId,
            ReferenceId: service.id
          })) as SelectedMedicalServiceType[]) || []
        }
        ErrorInformed={undefined}
        ViewOnly3erPartyServices={undefined}
        isOpen={isOpen}
        openPanel={() => undefined}
        dismissPanel={handleClosePanel}
        onSelect={onSelectService}
        onUnselect={onUnselectService}
      />
    </section>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    MedicalServices: state.MedicalServices,
    MedicalInsurances: state.MedicalInsurances,
    MedicalInsurancePlans: state.MedicalInsurancePlans
  };
};
const mapDispatchToProps = {
  ...MedicalServiceStore.actionCreators,
  ...MedicalInsuranceStore.actionCreators,
  ...AgreementServicesStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AgreementServiceListComponent);
